import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useOrgTenants } from "../../../hooks/resources.ts";
import NoSelectionPrompt from "../../../components/NoSelectionPrompt.tsx";
import { api } from "../../../api/client.ts";
import type { NotionConnection } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { userHasGlobalScope, userHasOrgScope } from "../../../auth/scopes.ts";
import { useAuth } from "../../../contexts/AuthContext.tsx";
import { useOrganization } from "../../../contexts/OrganizationContext.tsx";
import ClaudeIntegrationSection from "./components/ClaudeIntegrationSection.tsx";
import CrmIntegrationSection from "./components/CrmIntegrationSection.tsx";
import NotionIntegrationSection from "./components/NotionIntegrationSection.tsx";
import NotionScopesDialog from "./components/NotionScopesDialog.tsx";
import SlackIntegrationSection from "./components/SlackIntegrationSection.tsx";
import PageHeader from "../../../components/PageHeader.tsx";

export default function IntegrationsPage() {
  const queryClient = useQueryClient();
  const { selectedOrgId, currentOrg } = useOrganization();
  const { user } = useAuth();
  const canManageCredentials = userHasOrgScope(user, selectedOrgId, "org:manage_credentials");
  // The page is for organization admins; CRM connections are global-admin business.
  const canSeeCrm = userHasGlobalScope(user, "connections:read");

  // The integration an OAuth callback just returned to opens expanded. Read
  // before the effect below strips the params.
  const [oauthReturn] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("slack")) return "slack";
    if (params.get("notion")) return "notion";
    return null;
  });

  const [scopesConn, setScopesConn] = useState<NotionConnection | null>(null);

  const slackIntegration = useQuery({
    queryKey: keys.slackIntegration.byOrg(selectedOrgId ?? ""),
    queryFn: () => api.getSlackIntegration(selectedOrgId!),
    enabled: !!selectedOrgId,
  });

  const notionSetup = useQuery({
    queryKey: keys.notion.setupInfo(selectedOrgId ?? ""),
    queryFn: () => api.getNotionSetupInfo(selectedOrgId!),
    enabled: !!selectedOrgId,
  });

  const notionConnections = useQuery({
    queryKey: keys.notion.connections(selectedOrgId ?? ""),
    queryFn: () => api.getNotionConnections(selectedOrgId!),
    enabled: !!selectedOrgId,
  });

  // Only tenants the organization owns can be given Notion pages.
  const orgTenantLinks = useOrgTenants(selectedOrgId, { enabled: canManageCredentials });
  const ownedTenants = useMemo(
    () =>
      (orgTenantLinks.data ?? [])
        .filter((ot) => ot.is_primary)
        .map((ot) => ({ id: ot.tenant_id, name: ot.tenant_name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [orgTenantLinks.data],
  );

  const deleteSlackMutation = useMutation({
    mutationFn: () => api.deleteSlackIntegration(selectedOrgId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.slackIntegration.byOrg(selectedOrgId!),
      });
      toast.success("Slack disconnected");
    },
  });

  const deleteNotionMutation = useMutation({
    mutationFn: (connectionId: string) => api.deleteNotionConnection(selectedOrgId!, connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.notion.all });
      // Workflows' Notion choices and availability change with it.
      queryClient.invalidateQueries({ queryKey: keys.workflowDataType.all });
      queryClient.invalidateQueries({ queryKey: keys.agentDefinition.all });
      toast.success("Notion disconnected");
    },
  });

  // Consume the ?slack=connected|error and ?notion=connected|error|cancelled
  // params the OAuth callbacks put on the URL, toast the outcome, and strip them so
  // a refresh doesn't re-trigger the toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("slack");
    const notion = params.get("notion");
    // A fixed id: StrictMode runs this effect twice before the params are gone.
    const id = "integrations-oauth-return";
    if (status === "connected") {
      toast.success("Slack connected", { id });
    } else if (status === "error") {
      toast.error("Slack connection failed. Try again.", { id });
    } else if (notion === "connected") {
      toast.success("Notion connected. Choose which pages each tenant uses under Pages for tenants.", {
        id,
        duration: 8000,
      });
    } else if (notion === "error") {
      toast.error("Notion connection failed. Try again.", { id });
    } else if (notion === "cancelled") {
      toast("Notion connection cancelled.", { id });
    }
    if (status || notion) {
      params.delete("slack");
      params.delete("notion");
      const search = params.toString();
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${search ? `?${search}` : ""}`,
      );
    }
  }, []);

  if (!selectedOrgId) {
    return (
      <Box>
        <PageHeader title="Integrations" />
        <NoSelectionPrompt kind="org" />
      </Box>
    );
  }

  return (
    <>
      <PageHeader
        title="Integrations"
        description={`The tools ${currentOrg?.name ?? "this organization"} connects to ScaleupXQ`}
      />
      <Stack spacing={1.5}>
        {canSeeCrm && <CrmIntegrationSection organizationId={selectedOrgId} />}
        <ClaudeIntegrationSection organizationId={selectedOrgId} canManage={canManageCredentials} />
        <SlackIntegrationSection
          integration={slackIntegration.data}
          isLoading={slackIntegration.isLoading}
          deleting={deleteSlackMutation.isPending}
          defaultExpanded={oauthReturn === "slack"}
          onConnect={() => window.location.assign(api.slackInstallUrl(selectedOrgId))}
          onDisconnect={() => deleteSlackMutation.mutateAsync()}
        />
        <NotionIntegrationSection
          setupInfo={notionSetup.data}
          connections={notionConnections.data}
          isLoading={notionSetup.isLoading || notionConnections.isLoading}
          canManage={canManageCredentials}
          deletingId={deleteNotionMutation.isPending ? (deleteNotionMutation.variables ?? null) : null}
          defaultExpanded={oauthReturn === "notion"}
          onConnect={() => window.location.assign(api.notionInstallUrl(selectedOrgId))}
          onDisconnect={(c) => deleteNotionMutation.mutate(c.id)}
          onOpenScopes={setScopesConn}
        />
      </Stack>
      <NotionScopesDialog
        key={scopesConn?.id ?? "closed"}
        organizationId={selectedOrgId}
        connection={scopesConn}
        tenants={ownedTenants}
        onClose={() => setScopesConn(null)}
      />
    </>
  );
}
